import "./WidgetContainerComponent.scss";
import template from "./WidgetContainerComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {WidgetType} from "../../models/WidgetType";
import {isNotNull} from "../../../../js/util/UtilFunctions";
import {DashboardViewStore} from "../../store/DashboardViewStore";
import {CodeAndExchange} from "app/instruments/types/CodeAndExchange";
import {WidgetOptions} from "../../models/DashboardViewConfiguration";
import {WidgetDisplayMode} from "../../models/WidgetDisplayMode";

export const WidgetContainer = "tfDashboardWidgetContainer";

export function widgetContainer(): IComponentOptions {
    return {
        template,
        bindings: {
            widget: "<",
            displayMode: "<"
        },
        controllerAs: "ctrl",
        controller: WidgetContainerComponentController
    };
}

export class WidgetContainerComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set widget(widget: number | null) {
        if (widget != null) {
            this.widget$.next(widget);
        }
    }

    private readonly widget$ = new ReplaySubject<number>(1);

    /** @input */
    public readonly displayMode: WidgetDisplayMode | null;

    public readonly widgetType$ = new BehaviorSubject<WidgetType | null>(null);
    public readonly widgetConfig$ = new ReplaySubject<any | null>(1);
    public readonly widgetSecurity$ = new ReplaySubject<CodeAndExchange | null>(1);
    public readonly linked$ = new BehaviorSubject(false);

    private readonly widgetOptions$ = new ReplaySubject<WidgetOptions | null>(1);
    private readonly securityChanged$ = new ReplaySubject<CodeAndExchange>(1);
    private readonly configChanged$ = new ReplaySubject<any>(1);
    private readonly toggleLinked$ = new ReplaySubject<number>(1);
    private readonly create$ = new ReplaySubject<WidgetOptions>(1);
    private readonly remove$ = new ReplaySubject<boolean>(1);
    private readonly dashboard$: Observable<number>;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly dashboardViewStore: DashboardViewStore) {
        this.dashboard$ = this.dashboardViewStore
                              .currentDashboardNumber
                              .takeUntil(this.destroyed$);
    }

    public $onInit(): void {
        this.watchWidgetChanges();
        this.watchLinkedSecurity();
        this.watchSecurityChanged();
        this.watchConfigChanged();
        this.watchToggleLinked();
        this.watchCreate();
        this.watchRemove();
    }

    private watchWidgetChanges() {
        const currentWidgets$ = this.dashboardViewStore
                                    .currentDashboard
                                    .map((d) => d.layout)
                                    .map((l) => l.widgets);

        const source$ = Observable.combineLatest(currentWidgets$, this.widget$);

        source$.takeUntil(this.destroyed$)
               .map(([widgets, widget]) => widgets[widget])
               .subscribe((options) => {
                   if (options == null) {
                       this.widgetOptions$.next(null);
                       this.widgetType$.next(null);
                       this.widgetConfig$.next(null);
                       this.linked$.next(false);
                   } else {
                       this.widgetOptions$.next(options);
                       this.widgetType$.next(options.type);
                       this.widgetConfig$.next(options.config);
                       this.linked$.next(options.linked);
                   }
               });
    }

    private watchLinkedSecurity() {
        this.dashboardViewStore
            .currentDashboard
            .takeUntil(this.destroyed$)
            .map((dashboard) => dashboard.layout)
            .map((layout) => layout.security)
            .withLatestFrom(this.linked$)
            .subscribe(([security, linked]) => {
                if (linked) {
                    this.widgetSecurity$.next(security);
                } else {
                    this.widgetSecurity$.next(null);
                }
            });
    }

    private watchSecurityChanged() {
        this.securityChanged$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.linked$)
            .filter(([_, linked]) => linked)
            .map(([change]) => change)
            .withLatestFrom(this.dashboard$)
            .subscribe(([security, dashboard]) => this.dashboardViewStore.updateSecurity(dashboard, security));
    }

    private watchConfigChanged() {
        this.configChanged$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.widgetOptions$)
            .filter(([_, options]) => options != null)
            .map(([config, options]) => ({...options!, config}))
            .withLatestFrom(this.dashboard$, this.widget$)
            .subscribe(([options, dashboard, widget]) => this.dashboardViewStore.updateWidget(dashboard,
                                                                                              widget,
                                                                                              options));
    }

    private watchToggleLinked() {
        this.toggleLinked$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.widgetOptions$)
            .map(([_, options]) => options)
            .filter(isNotNull)
            .map((options) => ({...options, linked: !options.linked}))
            .withLatestFrom(this.dashboard$, this.widget$)
            .subscribe(([options, dashboard, widget]) => this.dashboardViewStore.updateWidget(dashboard,
                                                                                              widget,
                                                                                              options));
    }

    private watchCreate() {
        this.create$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.dashboard$, this.widget$)
            .subscribe(([options, dashboard, widget]) => this.dashboardViewStore.updateWidget(dashboard,
                                                                                              widget,
                                                                                              options));
    }

    private watchRemove() {
        this.remove$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.dashboard$, this.widget$)
            .subscribe(([_, dashboard, widget]) => this.dashboardViewStore.removeWidget(dashboard, widget));
    }

    public securityChanged(security: CodeAndExchange) {
        this.securityChanged$.next(security);
    }

    public configChanged(config: any) {
        this.configChanged$.next(config);
    }

    public toggleLinked() {
        this.toggleLinked$.next(1);
    }

    public create(widget: WidgetOptions) {
        this.create$.next(widget);
    }

    public remove() {
        this.remove$.next(true);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
